import { useMemo } from 'react';
import { useParams as _useParams, useLocation } from 'react-router-dom';

import { browserHistory } from '/app/history';
import type { QueryParams, Route, RouteName, RouteParams, RouteQueryParams } from './types';
import { findRouteByPathname, getSearchQuery } from './util';

type Q<T extends QueryParams | RouteName> = T extends RouteName ? RouteQueryParams[T] : T;

/**
 * Get current route object.
 */
export function useCurrentRoute(): (Route & { name: RouteName }) | undefined {
  const location = useLocation();

  return findRouteByPathname(location.pathname);
}

export function useQueryParams<T extends RouteName | QueryParams = QueryParams, QQ extends Q<T> = Q<T>>(): QQ {
  const location = useLocation();
  return useMemo(() => {
    return getSearchQuery(location?.search) as QQ;
  }, [location?.search]);
}

export function useQueryParamsMutable<T extends RouteName | QueryParams = QueryParams>(): [
  Q<T>,
  (mutate: (params: URLSearchParams) => undefined) => void,
] {
  const p = useQueryParams<T>();

  return [
    p,
    (mutate) => {
      const usp = new URLSearchParams(window.location.search);

      mutate(usp);

      browserHistory.replace({ search: usp.toString() });
    },
  ];
}

export function useParams<T extends Record<string, unknown>>() {
  return _useParams<RouteParams & T>();
}
